// copied from https://github.com/kikuchan/jmamap/blob/main/src/ol/JMATileLayer.ts
import type { LayerName, TargetTime } from './index';
import { fetchTargetTimes, getLayerInfo, getLayerURL } from './index';

import ImageTile from 'ol/ImageTile';
import type Tile from 'ol/Tile';
import { TileCoord } from 'ol/tilecoord.js';
import { Tile as TileLayer } from 'ol/layer.js';
import type { Options as TileLayerOptions } from 'ol/layer/Tile.js';
import { XYZ } from 'ol/source.js';
import type { Options as SourceOptions } from 'ol/source/XYZ.js';

const MINUTE_TO_MS = 60 * 1000;
const FUTURE_OFFSET_MINUTE = 60;
// 透明な1×1ピクセルの画像（エラー時のフォールバック用）
const TRANSPARENT_1X1_PIXEL_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// 下記リンク先のgetTileUrl関数を参考にした
// https://www.jma.go.jp/bosai/jmatile/js/chunk.jmatile.common.Leaflet_JMA_TileLayer__60f0c18c90d755fdba84.js
// 気象庁のタイルAPIでは偶数ズームレベルの画像のみ返却されるようになっている。
// そこで、奇数ズームレベルが指定された場合、各タイルでは一段階引いた偶数ズームレベルのタイル画像を
// リクエストしておき、表示時はそれを4分割して自分のタイル領域に該当する部分だけを描画する。
// (各タイルごとに、取得した画像がカバーする地理範囲はそのタイルがカバーすべき領域に対して縦横ともに
// 2倍となっているので、その一部のみを表示するよう調整する)
function tileUrlFunction(tileCoord: TileCoord, urlTemplate: string) : string {
  const z = tileCoord[0];
  const x = tileCoord[1];
  const y = -tileCoord[2] - 1;
  // 奇数ズームレベルの場合、一段階下の偶数ズームレベルのタイル画像を取得する
  if (z & 1) {
    const pz = z - 1;
    // ズームレベルを1段階下げる(引く)と、1つのタイルが4つのタイルに分割される(縦2倍×横2倍)ため、xとyを2で割って親タイルの座標を求める。
    const px = x >> 1;
    const py = y >> 1;
    return urlTemplate.replace('{z}', String(pz)).replace('{x}', String(px)).replace('{y}', String(py));
  }

  return urlTemplate.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}

function tileLoadFunction(tile: Tile, src: string): void {
  if (!(tile instanceof ImageTile)) return;

  const imageEl = tile.getImage() as HTMLImageElement;
  const [z, x, y] = tile.getTileCoord();
  const xyzY = -y - 1;

  // 偶数ズームではそのまま読み込む
  if ((z & 1) === 0) {
    imageEl.crossOrigin = 'anonymous';
    imageEl.src = src;
    return;
  }

  // 奇数ズームでは一段階下げた(引いた)ズームのタイル画像を取得したあと、
  // それを4分割した中の該当する1つを選択し、引き伸ばして表示する
  const qx = x & 1;
  const qy = xyzY & 1;

  const lessZoomedImage = new Image();
  lessZoomedImage.crossOrigin = 'anonymous';
  lessZoomedImage.onload = () => {
    const sw = lessZoomedImage.width / 2;
    const sh = lessZoomedImage.height / 2;
    const sx = qx * sw;
    const sy = qy * sh;

    const canvas = document.createElement('canvas');
    canvas.width = lessZoomedImage.width;
    canvas.height = lessZoomedImage.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(lessZoomedImage, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    imageEl.src = canvas.toDataURL('image/png');
  };
  lessZoomedImage.onerror = () => {
    // 透明な1×1ピクセルの画像
    imageEl.src = TRANSPARENT_1X1_PIXEL_IMAGE;
    console.error('Failed to load image', src);
  };
  lessZoomedImage.src = src;
}

export type LayerOptions = {
  isCloud?: boolean;
} & TileLayerOptions;

export class JMATileLayer extends TileLayer {
  sourceOpts: SourceOptions;
  isCloud: boolean;
  layerInfo: ReturnType<typeof getLayerInfo>;
  name: string;

  constructor(layerId: LayerName, opts: LayerOptions = {}, sourceOpts: SourceOptions = {}) {
    super(opts);
    this.name = layerId;
    this.layerInfo = getLayerInfo(layerId);
    if (!this.layerInfo) throw new Error('Unknown layer');

    this.isCloud = opts.isCloud ?? this.layerInfo.isCloud ?? false;
    this.sourceOpts = sourceOpts;
  }

  async getRadarTimePoints(minuteOffsets: number[]): Promise<TargetTime[]> {
    const targetTimes = await fetchTargetTimes(this.layerInfo.id);
    return this.filterRadarTimePoints(targetTimes, minuteOffsets);
  }

  filterRadarTimePoints(targetTimes: TargetTime[], minuteOffsets: number[]): TargetTime[] {
    if (targetTimes.length === 0) return [];

    const latestItem = targetTimes.find(item => item.tense === 'latest');
    if (!latestItem?.basetimeInDate) return [];

    const latestBaseTime = latestItem.basetimeInDate?.getTime() || 0;
    const filteredTimePoints: TargetTime[] = [];
    for (const minuteOffset of minuteOffsets) {
      let selectedItem: TargetTime | undefined;
      if (minuteOffset === FUTURE_OFFSET_MINUTE) {
        selectedItem = targetTimes[targetTimes.length - 1];
      } else {
        const targetTime = latestBaseTime + (minuteOffset * MINUTE_TO_MS);
        selectedItem = targetTimes.find(item =>
          item.validtimeInDate?.getTime() === targetTime,
        );
      }

      if (selectedItem?.validtime) {
        filteredTimePoints.push(selectedItem);
      }
    }

    return filteredTimePoints;
  }

  setTargetTime(targetTime: TargetTime): void {
    if (!targetTime) return;

    const url = getLayerURL(this.layerInfo.id, targetTime);
    if (!url) return;

    this.setSource(
      new XYZ({
        ...this.sourceOpts,
        url,
        attributions: this.layerInfo.attributions,
        attributionsCollapsible: false,
        minZoom: this.layerInfo.minZoom,
        maxZoom: this.layerInfo.maxZoom,
        tileUrlFunction: (tileCoord: TileCoord) => {
          return tileUrlFunction(tileCoord, url);
        },
        tileLoadFunction: tileLoadFunction,
      }),
    );
  }
}
